import { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import {
  Pill,
  Plus,
  Trash2,
  X,
  AlertTriangle,
  Clock,
  FileDown,
} from "lucide-react";
import { listStudents, updateStudentRegistry, getFieldTripMedications } from "../../lib/api";
import { toast } from "sonner";

export function MedicationsModule({ onClose }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStudent, setEditingStudent] = useState(null);
  const [allMedications, setAllMedications] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [studentsRes, medsRes] = await Promise.all([
          listStudents(),
          getFieldTripMedications().catch(() => ({ data: [] }))
        ]);
        setStudents(studentsRes.data || []);
        setAllMedications(medsRes.data || []);
      } catch (err) {
        console.error("Erreur chargement:", err);
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleUpdateMedications = async (studentId, medications) => {
    try {
      await updateStudentRegistry(studentId, { field_trip_medications: medications });
      toast.success("Médicaments mis à jour");
      setEditingStudent(null);
      // Refresh
      const response = await listStudents();
      setStudents(response.data || []);
    } catch (err) {
      console.error("Erreur mise à jour:", err);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Students with medications
  const studentsWithMeds = students.filter(s => 
    s.field_trip_medications?.length > 0 || s.health_info?.medications?.length > 0
  );

  const exportList = () => {
    let text = "LISTE DES MÉDICAMENTS POUR SORTIE SCOLAIRE\n";
    text += "=" .repeat(50) + "\n\n";
    
    studentsWithMeds.forEach(s => {
      const meds = s.field_trip_medications || s.health_info?.medications || [];
      if (meds.length > 0) {
        text += `${s.student_name}\n`;
        meds.forEach(m => {
          text += `  - ${m.name}`;
          if (m.dosage) text += ` (${m.dosage})`;
          if (m.frequency) text += ` - ${m.frequency}`;
          text += "\n";
          if (m.notes) text += `    Note: ${m.notes}\n`;
        });
        text += "\n";
      }
    });

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "medicaments_sortie.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Médicaments sorties</h2>
          <p className="text-sm text-slate-500">Traitements à emporter lors des sorties scolaires</p>
        </div>
        {studentsWithMeds.length > 0 && (
          <Button variant="outline" size="sm" onClick={exportList}>
            <FileDown className="w-4 h-4 mr-2" />
            Exporter la liste
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <Card className="bg-teal-50 border-teal-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-teal-700">{studentsWithMeds.length}</p>
            <p className="text-xs text-teal-600">Élèves avec médicaments</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-slate-700">
              {studentsWithMeds.reduce((sum, s) => sum + (s.field_trip_medications?.length || 0), 0)}
            </p>
            <p className="text-xs text-slate-500">Médicaments au total</p>
          </CardContent>
        </Card>
      </div>

      {/* Alerte si élèves avec médicaments */}
      {studentsWithMeds.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <strong>{studentsWithMeds.length} élève{studentsWithMeds.length > 1 ? "s" : ""}</strong> nécessite{studentsWithMeds.length > 1 ? "nt" : ""} un traitement lors des sorties.
              Pensez à préparer les médicaments avant chaque déplacement.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des élèves */}
      <ScrollArea className="h-[300px] sm:h-[350px]">
        <div className="space-y-2">
          {students.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Pill className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Aucun élève enregistré</p>
            </div>
          ) : (
            students.map((student) => {
              const medications = student.field_trip_medications || [];
              const isEditingThis = editingStudent === student.id;
              const hasMeds = medications.length > 0;

              return (
                <Card
                  key={student.id}
                  className={`transition-all ${hasMeds ? "border-teal-300 bg-teal-50/30" : ""}`}
                >
                  <CardContent className="p-3">
                    {isEditingThis ? (
                      <EditMedicationsForm
                        student={student}
                        onSave={(meds) => handleUpdateMedications(student.id, meds)}
                        onCancel={() => setEditingStudent(null)}
                      />
                    ) : (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasMeds ? "bg-teal-100" : "bg-slate-100"}`}>
                            <span className={`font-medium ${hasMeds ? "text-teal-700" : "text-slate-700"}`}>
                              {student.student_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800">{student.student_name}</p>
                            {hasMeds ? (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {medications.map((med, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-teal-50 text-teal-700 border-teal-300">
                                    <Pill className="w-3 h-3 mr-1" />
                                    {med.name}
                                    {med.dosage && <span className="ml-1 text-teal-500">({med.dosage})</span>}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">Aucun médicament</span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="self-end sm:self-auto"
                          onClick={() => setEditingStudent(student.id)}
                        >
                          Modifier
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function EditMedicationsForm({ student, onSave, onCancel }) {
  const [medications, setMedications] = useState(
    student.field_trip_medications || []
  );

  const addMedication = () => {
    setMedications(prev => [
      ...prev,
      { name: "", dosage: "", frequency: "", notes: "" }
    ]);
  };

  const updateMedication = (index, field, value) => {
    setMedications(prev => prev.map((med, i) => 
      i === index ? { ...med, [field]: value } : med
    ));
  };

  const removeMedication = (index) => {
    setMedications(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {medications.map((med, index) => (
          <Card key={index} className="bg-slate-50">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Médicament {index + 1}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-red-500 hover:text-red-700"
                  onClick={() => removeMedication(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  value={med.name}
                  onChange={(e) => updateMedication(index, "name", e.target.value)}
                  placeholder="Nom du médicament *"
                  className="text-sm"
                />
                <Input
                  value={med.dosage}
                  onChange={(e) => updateMedication(index, "dosage", e.target.value)}
                  placeholder="Dosage (ex: 5mg)"
                  className="text-sm"
                />
              </div>
              <Input
                value={med.frequency}
                onChange={(e) => updateMedication(index, "frequency", e.target.value)}
                placeholder="Fréquence (ex: 2x par jour, si besoin)"
                className="text-sm"
              />
              <Textarea
                value={med.notes}
                onChange={(e) => updateMedication(index, "notes", e.target.value)}
                placeholder="Notes (ex: à prendre avec de l'eau)"
                className="text-sm"
                rows={1}
              />
            </CardContent>
          </Card>
        ))}
      </div>

      <Button variant="outline" size="sm" className="w-full" onClick={addMedication}>
        <Plus className="w-4 h-4 mr-2" />
        Ajouter un médicament
      </Button>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Annuler
        </Button>
        <Button size="sm" onClick={() => onSave(medications)}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

export default MedicationsModule;
